import { Routes } from '@angular/router';
import {
  adminGuard,
  featureFlagGuard,
  guestGuard,
  memberGuard,
  superAdminGuard,
  superAdminGuestGuard,
} from './core/guards';
import { FeatureFlag } from './shared/feature-flags';

export const routes: Routes = [
  {
    path: 'inloggen',
    title: 'Inloggen · RENCO',
    canActivate: [guestGuard],
    loadComponent: () => import('./auth/login/login.component').then(m => m.LoginComponent),
  },
  {
    path: 'wachtwoord-vergeten',
    title: 'Wachtwoord vergeten · RENCO',
    loadComponent: () => import('./auth/forgot-password/forgot-password.component').then(m => m.ForgotPasswordComponent),
  },
  {
    path: 'wachtwoord-instellen',
    title: 'Wachtwoord instellen · RENCO',
    loadComponent: () => import('./auth/set-password/set-password.component').then(m => m.SetPasswordComponent),
  },
  {
    path: 'updates-afmelden',
    title: 'Updates per mail · RENCO',
    loadComponent: () =>
      import('./public/update-mail-unsubscribe/update-mail-unsubscribe.component').then(m => m.UpdateMailUnsubscribeComponent),
  },
  {
    path: 'geen-toegang',
    title: 'Geen toegang · RENCO',
    loadComponent: () => import('./auth/no-access/no-access.component').then(m => m.NoAccessComponent),
  },
  {
    path: 'leden',
    canActivate: [memberGuard],
    loadComponent: () => import('./member/member-shell.component').then(m => m.MemberShellComponent),
    children: [
      {
        path: '',
        pathMatch: 'full',
        title: 'Overzicht · RENCO',
        loadComponent: () =>
          import('./member/dashboard/member-dashboard.component').then(m => m.MemberDashboardComponent),
      },
      {
        path: 'vestigingen',
        title: 'Mijn vestiging(en) · RENCO',
        loadComponent: () =>
          import('./member/locations/member-locations.component').then(m => m.MemberLocationsComponent),
      },
      { path: 'gegevens', redirectTo: 'vestigingen' },
      { path: 'factuur', redirectTo: 'vestigingen' },
      {
        path: 'veelgestelde-vragen',
        title: 'Veelgestelde vragen · RENCO',
        loadComponent: () => import('./member/faq/member-faq.component').then(m => m.MemberFaqComponent),
      },
      {
        path: 'updates',
        title: 'Updates · RENCO',
        loadComponent: () => import('./member/updates/update-list.component').then(m => m.MemberUpdateListComponent),
      },
      {
        path: 'updates/:id',
        title: 'Update · RENCO',
        loadComponent: () =>
          import('./member/updates/update-detail.component').then(m => m.MemberUpdateDetailComponent),
      },
      {
        path: 'vragen',
        title: 'Vragen · RENCO',
        loadComponent: () =>
          import('./member/questions/question-list.component').then(m => m.MemberQuestionListComponent),
      },
      {
        path: 'vragen/nieuw',
        title: 'Nieuwe vraag · RENCO',
        loadComponent: () => import('./member/questions/ask-question.component').then(m => m.AskQuestionComponent),
      },
      {
        path: 'vragen/:id',
        title: 'Vraag · RENCO',
        loadComponent: () =>
          import('./member/questions/question-detail.component').then(m => m.MemberQuestionDetailComponent),
      },
    ],
  },
  {
    path: 'beheer',
    canActivate: [adminGuard],
    loadComponent: () => import('./admin/admin-shell.component').then(m => m.AdminShellComponent),
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'klanten' },
      {
        path: 'klanten',
        title: 'Klanten · Beheer · RENCO',
        loadComponent: () => import('./admin/customers/customer-list.component').then(m => m.CustomerListComponent),
      },
      {
        path: 'klanten/:id',
        title: 'Klant · Beheer · RENCO',
        loadComponent: () => import('./admin/customers/customer-detail.component').then(m => m.CustomerDetailComponent),
      },
      {
        path: 'vragen',
        title: 'Vragen · Beheer · RENCO',
        loadComponent: () =>
          import('./admin/questions/question-inbox.component').then(m => m.QuestionInboxComponent),
      },
      {
        path: 'vragen/:id',
        title: 'Vraag · Beheer · RENCO',
        loadComponent: () =>
          import('./admin/questions/admin-question-detail.component').then(m => m.AdminQuestionDetailComponent),
      },
      {
        path: 'veelgestelde-vragen',
        title: 'Veelgestelde vragen · Beheer · RENCO',
        loadComponent: () => import('./admin/faq/faq-list.component').then(m => m.FaqListComponent),
      },
      {
        path: 'beheerders',
        title: 'Beheerders · Beheer · RENCO',
        loadComponent: () =>
          import('./admin/administrators/administrator-list.component').then(m => m.AdministratorListComponent),
      },
      {
        path: 'pers',
        title: 'Pers · Beheer · RENCO',
        canActivate: [featureFlagGuard(FeatureFlag.PRESS_CONTACT)],
        loadComponent: () => import('./admin/press/press-contact.component').then(m => m.PressContactComponent),
      },
      {
        path: 'wachtwoord',
        title: 'Wachtwoord wijzigen · Beheer · RENCO',
        loadComponent: () =>
          import('./admin/account/change-password.component').then(m => m.ChangePasswordComponent),
      },
      {
        path: 'updates',
        title: 'Updates · Beheer · RENCO',
        loadComponent: () => import('./admin/updates/update-list.component').then(m => m.UpdateListComponent),
      },
      {
        path: 'updates/nieuw',
        title: 'Nieuwe update · Beheer · RENCO',
        loadComponent: () => import('./admin/updates/update-editor.component').then(m => m.UpdateEditorComponent),
      },
      {
        path: 'updates/:id/mail',
        title: 'Mail per lid · Beheer · RENCO',
        canActivate: [featureFlagGuard(FeatureFlag.UPDATE_MAILS)],
        loadComponent: () =>
          import('./admin/updates/update-mail-recipients.component').then(m => m.UpdateMailRecipientsComponent),
      },
      {
        path: 'updates/:id',
        title: 'Update · Beheer · RENCO',
        loadComponent: () => import('./admin/updates/update-editor.component').then(m => m.UpdateEditorComponent),
      },
    ],
  },
  // Enrollment (brok 5), with its own layout.
  // Superbeheer for Rick and Lars: its own login and key, apart from beheer (docs: Claude Docs "Superbeheer RENCO").
  {
    path: 'superadmin/inloggen',
    title: 'Superbeheer · RENCO',
    canActivate: [superAdminGuestGuard],
    loadComponent: () =>
      import('./super-admin/login/super-admin-login.component').then(m => m.SuperAdminLoginComponent),
  },
  {
    path: 'superadmin',
    canActivate: [superAdminGuard],
    loadComponent: () => import('./super-admin/super-admin-shell.component').then(m => m.SuperAdminShellComponent),
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'overzicht' },
      {
        path: 'overzicht',
        title: 'Overzicht · Superbeheer',
        loadComponent: () =>
          import('./super-admin/overview/overview.component').then(m => m.SuperAdminOverviewComponent),
      },
      {
        path: 'auditlog',
        title: 'Auditlog · Superbeheer',
        loadComponent: () =>
          import('./super-admin/audit-log/audit-log.component').then(m => m.SuperAdminAuditLogComponent),
      },
      {
        path: 'crons',
        title: 'Crons · Superbeheer',
        loadComponent: () => import('./super-admin/crons/crons.component').then(m => m.SuperAdminCronsComponent),
      },
      {
        path: 'moneybird',
        title: 'Moneybird · Superbeheer',
        loadComponent: () =>
          import('./super-admin/moneybird/moneybird.component').then(m => m.SuperAdminMoneybirdComponent),
      },
      {
        path: 'bestuur',
        title: 'Bestuur · Superbeheer',
        loadComponent: () => import('./super-admin/board/board.component').then(m => m.SuperAdminBoardComponent),
      },
      {
        path: 'vestigingen',
        title: 'Vestigingen · Superbeheer',
        loadComponent: () =>
          import('./super-admin/locations/locations.component').then(m => m.SuperAdminLocationsComponent),
      },
      {
        path: 'mailverbruik',
        title: 'Mailverbruik · Superbeheer',
        loadComponent: () =>
          import('./super-admin/mail-usage/mail-usage.component').then(m => m.SuperAdminMailUsageComponent),
      },
      {
        path: 'feature-flags',
        title: 'Feature flags · Superbeheer',
        loadComponent: () =>
          import('./super-admin/feature-flags/feature-flags.component').then(m => m.SuperAdminFeatureFlagsComponent),
      },
      {
        path: 'updates-per-mail',
        title: 'Updates per mail · Superbeheer',
        loadComponent: () =>
          import('./super-admin/update-mails/update-mails.component').then(m => m.SuperAdminUpdateMailsComponent),
      },
    ],
  },
  {
    path: 'inschrijven',
    title: 'Inschrijven · RENCO',
    loadComponent: () => import('./enrollment/enrollment-start.component').then(m => m.EnrollmentStartComponent),
  },
  {
    path: 'inschrijven/bevestiging',
    title: 'Inschrijving ontvangen · RENCO',
    loadComponent: () => import('./enrollment/confirmation.component').then(m => m.ConfirmationComponent),
  },
  {
    path: 'inschrijven/:token',
    loadComponent: () => import('./enrollment/enrollment-flow.component').then(m => m.EnrollmentFlowComponent),
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'stap-1' },
      // Componentless: the flow renders the steps itself (see EnrollmentFlowComponent), the routes keep the addresses.
      { path: 'stap-1', title: 'Inschrijven: bedrijfsgegevens · RENCO', children: [] },
      { path: 'stap-2', title: 'Inschrijven: verkooppunt en omzet · RENCO', children: [] },
      { path: 'stap-3', title: 'Inschrijven: controleren en versturen · RENCO', children: [] },
    ],
  },
  // Public website (brok 4); last, so the routes above are matched first.
  {
    path: '',
    loadComponent: () => import('./public/public-shell.component').then(m => m.PublicShellComponent),
    children: [
      {
        path: '',
        pathMatch: 'full',
        title: 'RENCO · Samen voor een redelijke en nette compensatie',
        loadComponent: () => import('./public/home/home-page.component').then(m => m.HomePageComponent),
      },
      {
        path: 'privacyverklaring',
        title: 'Privacyverklaring · RENCO',
        loadComponent: () => import('./public/privacy/privacy-page.component').then(m => m.PrivacyPageComponent),
      },
      {
        path: 'waarom-lid-worden',
        title: 'Waarom lid worden · RENCO',
        loadComponent: () => import('./public/why-member/why-member-page.component').then(m => m.WhyMemberPageComponent),
      },
      {
        path: 'hoe-het-werkt',
        title: 'Hoe het werkt · RENCO',
        loadComponent: () => import('./public/how-it-works/how-it-works-page.component').then(m => m.HowItWorksPageComponent),
      },
      {
        path: 'kosten',
        title: 'Wat kost het · RENCO',
        loadComponent: () => import('./public/costs/costs-page.component').then(m => m.CostsPageComponent),
      },
      {
        path: 'vragen',
        title: 'Vragen en contact · RENCO',
        loadComponent: () =>
          import('./public/questions/questions-page.component').then(m => m.QuestionsPageComponent),
      },
      // Questions and contact used to be two pages; the old addresses keep working.
      { path: 'veelgestelde-vragen', pathMatch: 'full', redirectTo: 'vragen' },
      { path: 'contact', pathMatch: 'full', redirectTo: 'vragen' },
      // Last: every address nothing above knows, also under /leden and /beheer.
      {
        path: '**',
        title: 'Pagina niet gevonden · RENCO',
        loadComponent: () => import('./public/not-found/not-found-page.component').then(m => m.NotFoundPageComponent),
      },
    ],
  },
];
